import { getConfig } from '@app/config';
import { createQueryParamString, FilterParam, initEmptyItemList, getPageCount } from '@app/utils/util';
import { ItemList, LinkedEvent } from '@store/domain';
import { format } from 'date-fns';
import { get, writable } from 'svelte/store';

/* Store for fetching & storing events (events change daily or hourly, consider short time cache) */

// Stores
export const eventList = writable<ItemList<LinkedEvent>>(initEmptyItemList<LinkedEvent>());
export const selectedEvent = writable<LinkedEvent | null>(null);
export const isLoading = writable<boolean>(false);

export const pageNumber = writable<number>(1);
export const pageCount = writable<number>(0);

// Internal consts
const { baseUrl } = getConfig().api;
const { events } = getConfig().modules;
const { dateFormat } = getConfig().site;

/**
 * Initial load from API
 */
export async function loadEvents(params: FilterParam) {
  const { area } = params;
  const queryP = {};
  if (area) {
    Object.assign(queryP, {
      location: area
    });
  }

  if (!get(isLoading)) {
    const params = createQueryParamString(Object.assign(queryP, events.queryParams));
    const url = `${baseUrl}/${events.apiPath}/event/?${params}`;

    console.log('Loading events...');
    isLoading.set(true);
    const fetchedEvents: ItemList<LinkedEvent> = await fetch(url).then(res => res.json());
    eventList.set({
      meta: fetchedEvents.meta,
      data: fetchedEvents.data
    });
    pageCount.set(getPageCount(fetchedEvents.meta.count, parseInt(events.queryParams['page_size'])));
    pageNumber.set(1);
    isLoading.set(false);
  }
}

export function loadSingleEvent(eventId: string) {
  const eventsFromStore: ItemList<LinkedEvent> = get(eventList);
  const eventFound = eventsFromStore.data.find(e => e.id === eventId);

  if (eventFound) {
    console.log(`Event with ID ${eventId} found in store`);
    selectedEvent.set(eventFound);
  } else {
    if (!get(isLoading)) {
      const params = createQueryParamString({ include: 'location' });
      const url = `${baseUrl}/${events.apiPath}/event/${eventId}/?${params}`;

      console.log(`Loading event with ID '${eventId}'...`);

      isLoading.set(true);
      fetch(url)
        .then(res => res.json())
        .then(res => {
          selectedEvent.set(res);
          isLoading.set(false);
        })
        .catch(err => {
          console.error(err);
          isLoading.set(false);
        });
    }
  }
}

export async function filterEvents(params: FilterParam) {
  const { audience, keywords, area, searchQuery, startDate } = params;
  let queryParams = {};

  if (startDate) {
    Object.assign(queryParams, {
      start: format(startDate, 'yyyy-MM-dd')
    });
  }

  if (audience) {
    const keywordsJoined = audience.join(',');
    Object.assign(queryParams, {
      audience: keywordsJoined
    });
  }

  if (keywords) {
    const keywordsJoined = keywords.join(',');
    Object.assign(queryParams, {
      keyword: keywordsJoined
    });
  }

  if (area) {
    Object.assign(queryParams, {
      location: area
    });
  }

  if (searchQuery) {
    Object.assign(queryParams, {
      text: searchQuery
    });
  }

  queryParams = Object.assign(events.queryParams, queryParams);

  const queryParamString = createQueryParamString(queryParams);
  const url = `${baseUrl}/${events.apiPath}/event/?${queryParamString}`;
  console.log('Searching events...', url);
  isLoading.set(true);
  const fetchedEvents: ItemList<LinkedEvent> = await fetch(url).then(res => res.json());
  eventList.set({
    meta: fetchedEvents.meta,
    data: fetchedEvents.data
  });
  isLoading.set(false);
  pageCount.set(getPageCount(fetchedEvents.meta.count, parseInt(events.queryParams['page_size'])));
  pageNumber.set(1);
}

export async function getNextEventPage() {
  const currentEvents: ItemList<LinkedEvent> = get(eventList);
  if (currentEvents.meta.next != null) {
    isLoading.set(true);
    fetch(currentEvents.meta.next)
      .then(res => res.json())
      .then(res => {
        eventList.set(res);
        isLoading.set(false);
        const page = get(pageNumber) + 1;
        pageNumber.set(page);
      })
      .catch(err => {
        console.error(err);
        isLoading.set(false);
      });
  }
}

export async function getPreviousEventPage() {
  const currentEvents: ItemList<LinkedEvent> = get(eventList);
  if (currentEvents.meta.previous != null) {
    isLoading.set(true);
    fetch(currentEvents.meta.previous)
      .then(res => res.json())
      .then(res => {
        eventList.set(res);
        isLoading.set(false);
        const page = get(pageNumber) - 1;
        pageNumber.set(page);
      })
      .catch(err => {
        console.error(err);
        isLoading.set(false);
      });
  }
}

export async function getCertainEventPage(page: number) {
  const currentEvents: ItemList<LinkedEvent> = get(eventList);
  if (currentEvents.meta.next != null || currentEvents.meta.previous != null) {
    var url = new URL((currentEvents.meta.next != null ? currentEvents.meta.next : currentEvents.meta.previous) as string);
    url.searchParams.set('page', page.toString());
    isLoading.set(true);
    fetch(url.href)
      .then(res => res.json())
      .then(res => {
        eventList.set(res);
        isLoading.set(false);
        pageNumber.set(page);
      })
      .catch(err => {
        console.error(err);
        isLoading.set(false);
      });
  } else {
    return null;
  }
}
